import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { useState } from "react";
import {
	getGetUmbracoApiV1MembersCurrentQueryKey,
	useGetUmbracoApiV1MembersCurrent,
	usePostUmbracoApiV1MembersLogout,
} from "#/_generated/umbraco/authentication-api/features/members/members.ts";
import type {
	CustomizableLinkUmbBlockPartialElementModel,
	LoginLogoutUmbBlockElementModel,
	LoginLogoutUmbBlockSettingsElementModel,
} from "#/_generated/umbraco/delivery-api/models";
import { LoginLogout } from "#/_packages/umbrella/features/login-logout/components/login-logout";
import type { BlockItemProps } from "#/_packages/umbrella/integrations/umbraco/types";
import { RenderBlock } from "#/_packages/umbrella/integrations/umbraco/utils/blockgrid-item-renderer.tsx";
import { m } from "#/paraglide/messages";
import CustomizableLinkUmbBlock from "./customizable-link-umb-block";

type LoginLogoutBlockProps = BlockItemProps & {
	content: LoginLogoutUmbBlockElementModel;
	settings: LoginLogoutUmbBlockSettingsElementModel;
};

export default function LoginLogoutUmbBlock(props: LoginLogoutBlockProps) {
	const isPreview = !!useRouter().options.context.previewBlock;
	return isPreview ? (
		<LoginLogout preview className={props.className} />
	) : (
		<Default {...props} />
	);
}

function Default({ className, content }: LoginLogoutBlockProps) {
	const { data: currentUser } = useGetUmbracoApiV1MembersCurrent();
	const queryClient = useQueryClient();
	const router = useRouter();
	const { mutateAsync: signOut, isPending } =
		usePostUmbracoApiV1MembersLogout();
	const [failed, setFailed] = useState(false);

	const logout = async () => {
		setFailed(false);
		try {
			await signOut();
		} catch {
			setFailed(true);
			return;
		}
		queryClient.setQueryData(getGetUmbracoApiV1MembersCurrentQueryKey(), {
			...currentUser,
			data: null,
		});
		await queryClient.invalidateQueries({
			queryKey: getGetUmbracoApiV1MembersCurrentQueryKey(),
		});
		await router.navigate({ to: "/", reloadDocument: true });
	};

	if (!currentUser?.data?.key) {
		return (
			<>
				{content.properties?.login?.items.map((block) => (
					<div key={block.content.id}>{RenderBlock(block, className)}</div>
				))}
			</>
		);
	}
	return (
		<>
			{content.properties?.logout?.items.map((block) =>
				block.content.contentType === "customizableLinkUmbBlockPartial" ? (
					<CustomizableLinkUmbBlock
						key={block.content.id}
						content={
							block.content as CustomizableLinkUmbBlockPartialElementModel
						}
						className={className}
						onPress={() => {
							void logout();
						}}
						isPending={isPending}
					/>
				) : null,
			)}
			{failed ? <p role="alert">{m.page_error_heading()}</p> : null}
		</>
	);
}
